import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const GlobeAdvanced: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const globeRef = useRef<{
    scene: THREE.Scene | null;
    camera: THREE.PerspectiveCamera | null;
    renderer: THREE.WebGLRenderer | null;
    globe: THREE.Mesh | null;
    points: THREE.Points | null;
    connections: THREE.Line[] | null;
    shootingStars: THREE.Mesh[] | null;
    lightParticles: THREE.Points | null;
  }>({
    scene: null,
    camera: null,
    renderer: null,
    globe: null,
    points: null,
    connections: null,
    shootingStars: null,
    lightParticles: null,
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;

    // Scene setup
    const scene = new THREE.Scene();
    globeRef.current.scene = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.z = 300;
    globeRef.current.camera = camera;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    containerRef.current.appendChild(renderer.domElement);
    globeRef.current.renderer = renderer;

    // Globe setup
    const radius = 100;
    const segments = 64;
    const geometry = new THREE.SphereGeometry(radius, segments, segments);
    
    // Create globe material with enhanced emissive glow
    const material = new THREE.MeshPhongMaterial({
      color: 0x0077be,
      emissive: 0x0a3553,
      transparent: true,
      opacity: 0.9,
      shininess: 15,
      wireframe: false,
    });
    
    const globe = new THREE.Mesh(geometry, material);
    scene.add(globe);
    globeRef.current.globe = globe;

    // Add enhanced atmospheric glow with stronger emission
    const atmosphereGeometry = new THREE.SphereGeometry(radius * 1.15, segments, segments);
    const atmosphereMaterial = new THREE.MeshPhongMaterial({
      color: 0x0ea5e9,
      emissive: 0x0ea5e9,
      emissiveIntensity: 0.3,
      side: THREE.BackSide,
      transparent: true,
      opacity: 0.2,
    });
    const atmosphere = new THREE.Mesh(atmosphereGeometry, atmosphereMaterial);
    scene.add(atmosphere);
    
    // Add outer atmospheric glow for extended effect
    const outerAtmosphereGeometry = new THREE.SphereGeometry(radius * 1.3, segments, segments);
    const outerAtmosphereMaterial = new THREE.MeshPhongMaterial({
      color: 0x0ea5e9,
      emissive: 0x0ea5e9,
      emissiveIntensity: 0.1,
      side: THREE.BackSide,
      transparent: true,
      opacity: 0.1,
    });
    const outerAtmosphere = new THREE.Mesh(outerAtmosphereGeometry, outerAtmosphereMaterial);
    scene.add(outerAtmosphere);

    // Add random points on the globe (representing data centers/hubs)
    // Increasing the number of points for a denser appearance
    const pointsGeometry = new THREE.BufferGeometry();
    const pointsCount = 50; // Increased from 30 to 50
    const positions = [];
    const pointsData = [];

    for (let i = 0; i < pointsCount; i++) {
      // Create random points on the globe
      const phi = Math.acos(-1 + Math.random() * 2);
      const theta = Math.random() * Math.PI * 2;
      
      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.sin(phi) * Math.sin(theta);
      const z = radius * Math.cos(phi);
      
      positions.push(x, y, z);
      pointsData.push({ x, y, z, size: Math.random() * 2 + 1, speed: Math.random() * 0.03 + 0.01 });
    }
    
    pointsGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    
    // Brighter points with larger sizes
    const pointsMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 3.5, // Increased from 2.5
      transparent: true,
      opacity: 0.9, // Increased from 0.8
      sizeAttenuation: true,
    });
    
    const points = new THREE.Points(pointsGeometry, pointsMaterial);
    scene.add(points);
    globeRef.current.points = points;
    
    // Create more connections between random points
    const connections: THREE.Line[] = [];
    for (let i = 0; i < 25; i++) { // Increased from 15 to 25
      const startPoint = Math.floor(Math.random() * pointsCount);
      let endPoint = Math.floor(Math.random() * pointsCount);
      while (startPoint === endPoint) {
        endPoint = Math.floor(Math.random() * pointsCount);
      }
      
      const startPosition = new THREE.Vector3(
        positions[startPoint * 3],
        positions[startPoint * 3 + 1],
        positions[startPoint * 3 + 2]
      );
      
      const endPosition = new THREE.Vector3(
        positions[endPoint * 3],
        positions[endPoint * 3 + 1],
        positions[endPoint * 3 + 2]
      );
      
      // Create a curve between the two points, elevating it slightly
      const curvePoints = [];
      const segments = 20;
      
      for (let j = 0; j <= segments; j++) {
        const t = j / segments;
        
        // Linear interpolation
        const x = startPosition.x + (endPosition.x - startPosition.x) * t;
        const y = startPosition.y + (endPosition.y - startPosition.y) * t;
        const z = startPosition.z + (endPosition.z - startPosition.z) * t;
        
        // Normalize to get point on the direct line, then add a bit of height
        const point = new THREE.Vector3(x, y, z);
        const normalizedPoint = point.clone().normalize();
        
        // Add elevation to the curve (maximum at the middle)
        const elevation = Math.sin(t * Math.PI) * (radius * 0.2); // Increased from 0.15 to 0.2
        point.copy(normalizedPoint.multiplyScalar(radius + elevation));
        
        curvePoints.push(point);
      }
      
      const curve = new THREE.CatmullRomCurve3(curvePoints);
      const curveGeometry = new THREE.BufferGeometry().setFromPoints(curve.getPoints(50));
      
      // Brighter and more visible connection lines
      const material = new THREE.LineBasicMaterial({
        color: 0x00ffff,
        transparent: true,
        opacity: 0.6 + Math.random() * 0.4, // Increased overall opacity
        linewidth: 1,
      });
      
      const line = new THREE.Line(curveGeometry, material);
      scene.add(line);
      connections.push(line);
    }
    globeRef.current.connections = connections;

    // Create shining light particles around the globe
    const particleCount = 300; // Large number of particles for dense effect
    const lightParticleGeometry = new THREE.BufferGeometry();
    const lightParticlePositions = [];
    const lightParticleColors = [];
    const lightParticleSizes = [];
    const lightParticleData = [];
    
    // Add particles in spherical coordinates for better distribution around globe
    for (let i = 0; i < particleCount; i++) {
      // Distance from center (distribute between 1.2x and 1.8x radius)
      const distance = radius * (1.2 + Math.random() * 0.6);
      
      // Create random angles for spherical distribution
      const phi = Math.acos(-1 + Math.random() * 2); // Polar angle
      const theta = Math.random() * Math.PI * 2;     // Azimuthal angle
      
      // Convert to Cartesian coordinates
      const x = distance * Math.sin(phi) * Math.cos(theta);
      const y = distance * Math.sin(phi) * Math.sin(theta);
      const z = distance * Math.cos(phi);
      
      lightParticlePositions.push(x, y, z);
      
      // Color variations - blue/cyan with intensity variations
      const r = 0.3 + Math.random() * 0.1;
      const g = 0.8 + Math.random() * 0.2;
      const b = 1.0;
      lightParticleColors.push(r, g, b);
      
      // Vary particle size for more natural look
      const size = 1.5 + Math.random() * 3;
      lightParticleSizes.push(size);
      
      // Store animation data for each particle
      lightParticleData.push({
        originalX: x,
        originalY: y,
        originalZ: z,
        pulseFactor: 1 + Math.random() * 0.3, // How much it pulses
        pulseSpeed: 0.002 + Math.random() * 0.003, // Speed of pulsing
        pulseOffset: Math.random() * Math.PI * 2, // Offset for varied timing
        orbitSpeed: (0.001 + Math.random() * 0.002) * (Math.random() > 0.5 ? 1 : -1), // Random orbit speed & direction
        orbitRadius: 3 + Math.random() * 5, // Small random orbit radius
        orbitAxis: Math.floor(Math.random() * 3) // Which axis to orbit around (0=x, 1=y, 2=z)
      });
    }
    
    // Create buffer attributes
    lightParticleGeometry.setAttribute('position', new THREE.Float32BufferAttribute(lightParticlePositions, 3));
    lightParticleGeometry.setAttribute('color', new THREE.Float32BufferAttribute(lightParticleColors, 3));
    lightParticleGeometry.setAttribute('size', new THREE.Float32BufferAttribute(lightParticleSizes, 1));
    
    // Create particle shader material for better looking particles
    const particleShaderMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
      },
      vertexShader: `
        attribute float size;
        attribute vec3 color;
        varying vec3 vColor;
        uniform float time;
        void main() {
          vColor = color;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mvPosition;
          gl_PointSize = size * (300.0 / -mvPosition.z) * (0.7 + 0.3 * sin(time + position.x + position.y + position.z));
        }
      `,
      fragmentShader: `
        varying vec3 vColor;
        void main() {
          float dist = length(gl_PointCoord - vec2(0.5, 0.5));
          if (dist > 0.5) discard;
          vec3 glow = vColor * (1.0 - (dist * 1.2));
          gl_FragColor = vec4(glow, 1.0 - (dist * 1.5));
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    
    // Create the particle system
    const lightParticles = new THREE.Points(lightParticleGeometry, particleShaderMaterial);
    scene.add(lightParticles);
    globeRef.current.lightParticles = lightParticles;

    // Enhanced lighting
    const ambientLight = new THREE.AmbientLight(0x404040, 1.5); // Brighter ambient light
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2); // Brighter directional light
    directionalLight.position.set(1, 1, 1);
    scene.add(directionalLight);
    
    const pointLight = new THREE.PointLight(0x00ffff, 1.5, 350); // Brighter point light with greater range
    pointLight.position.set(150, 100, 50);
    scene.add(pointLight);

    // Add a second colored point light for more dramatic lighting
    const pointLight2 = new THREE.PointLight(0x0077be, 1, 300);
    pointLight2.position.set(-150, -100, 100);
    scene.add(pointLight2);

    // Animation loop
    let animationId: number;
    let tick = 0;
    const animate = () => {
      tick++;
      animationId = requestAnimationFrame(animate);
      
      // Rotate the globe slowly
      if (globeRef.current.globe) {
        globeRef.current.globe.rotation.y += 0.002;
      }
      
      // Animate light particles
      if (globeRef.current.lightParticles && globeRef.current.scene) {
        const positions = globeRef.current.lightParticles.geometry.attributes.position.array;
        const time = Date.now() * 0.001; // Current time for animation
        
        // Update each light particle position
        for (let i = 0; i < positions.length / 3; i++) {
          const index = i * 3;
          const data = lightParticleData[i];
          
          if (!data) continue;
          
          // Get original position
          let x = data.originalX;
          let y = data.originalY;
          let z = data.originalZ;
          
          // Add pulse effect
          const pulseValue = Math.sin(time * data.pulseSpeed + data.pulseOffset) * data.pulseFactor;
          
          // Apply orbital motion based on chosen axis
          const angle = time * data.orbitSpeed;
          if (data.orbitAxis === 0) { // Orbit around X axis
            const originalY = y;
            y = y * Math.cos(angle) - z * Math.sin(angle);
            z = originalY * Math.sin(angle) + z * Math.cos(angle);
          } else if (data.orbitAxis === 1) { // Orbit around Y axis
            const originalX = x;
            x = x * Math.cos(angle) - z * Math.sin(angle);
            z = originalX * Math.sin(angle) + z * Math.cos(angle);
          } else { // Orbit around Z axis
            const originalX = x;
            x = x * Math.cos(angle) - y * Math.sin(angle);
            y = originalX * Math.sin(angle) + y * Math.cos(angle);
          }
          
          // Apply slight pulsing in and out from center
          const direction = new THREE.Vector3(x, y, z).normalize();
          x += direction.x * pulseValue * 2;
          y += direction.y * pulseValue * 2;
          z += direction.z * pulseValue * 2;
          
          // Update position
          positions[index] = x;
          positions[index + 1] = y;
          positions[index + 2] = z;
        }
        
        // Update the geometry
        globeRef.current.lightParticles.geometry.attributes.position.needsUpdate = true;
        
        // Update shader uniforms if using shader material
        if ((globeRef.current.lightParticles.material as any).uniforms?.time) {
          (globeRef.current.lightParticles.material as any).uniforms.time.value = time;
        }
      }
      
      // Animate shooting stars if they exist
      if (globeRef.current.shootingStars && Array.isArray(globeRef.current.shootingStars)) {
        globeRef.current.shootingStars.forEach((star: THREE.Mesh) => {
          if (!(star as any).active) {
            // Check if delay counter is active
            if ((star as any).delay > 0) {
              (star as any).delay--;
              return;
            }
            
            // Reset star position when it becomes active
            const startPos = new THREE.Vector3(
              (Math.random() - 0.5) * 600,
              (Math.random() - 0.5) * 600,
              (Math.random() - 0.5) * 600
            );
            startPos.normalize().multiplyScalar(250 + Math.random() * 150);
            star.position.copy(startPos);
            
            // Reset velocity
            (star as any).velocity = new THREE.Vector3(
              -startPos.x * (0.001 + Math.random() * 0.001),
              -startPos.y * (0.001 + Math.random() * 0.001),
              -startPos.z * (0.001 + Math.random() * 0.001)
            );
            
            (star as any).active = true;
          }
          
          // Move the star according to its velocity
          star.position.add((star as any).velocity);
          
          // Check if the star is close to the center
          const distanceToCenter = star.position.length();
          if (distanceToCenter < 50) {
            // Deactivate the star and set a new delay
            (star as any).active = false;
            (star as any).delay = Math.floor(Math.random() * 200) + 100;
          }
          
          // Add trail effect by scaling based on distance
          const scale = 0.5 + Math.min(1, Math.max(0, (distanceToCenter - 50) / 200)) * 1.5;
          star.scale.set(scale, scale, scale * 3); // Elongate in direction of travel
          
          // Orient the elongation in the direction of travel
          if ((star as any).velocity.length() > 0) {
            const lookAtPos = new THREE.Vector3().copy(star.position).add((star as any).velocity);
            star.lookAt(lookAtPos);
          }
          
          // Make stars fade as they approach the globe
          const opacity = Math.min(1, Math.max(0, (distanceToCenter - 50) / 100));
          if (star.material instanceof THREE.MeshBasicMaterial) {
            star.material.opacity = opacity;
          }
        });
      }
      
      // Camera movement - smoother and more subtle
      if (globeRef.current.camera && globeRef.current.scene && globeRef.current.renderer) {
        globeRef.current.camera.position.x = 200 * Math.sin(Date.now() * 0.00005);
        globeRef.current.camera.position.z = 200 * Math.cos(Date.now() * 0.00005);
        globeRef.current.camera.lookAt(globeRef.current.scene.position);
        
        globeRef.current.renderer.render(globeRef.current.scene, globeRef.current.camera);
      }
    };
    
    animate();

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current || !globeRef.current.camera || !globeRef.current.renderer) return;
      
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      
      globeRef.current.camera.aspect = width / height;
      globeRef.current.camera.updateProjectionMatrix();
      globeRef.current.renderer.setSize(width, height);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      if (globeRef.current.renderer && containerRef.current) {
        containerRef.current.removeChild(globeRef.current.renderer.domElement);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="globe-container w-full h-full rounded-lg" />
  );
};

export default GlobeAdvanced; 